// News post text: a small markdown subset rendered as React elements, never as HTML.
// "#", "##", "###" lines are headings (one level below the post title and down),
// "-" or "*" lines are list items, blank lines separate paragraphs, and line breaks
// inside a paragraph are kept. The server renders the same rules for crawlers
// (apps/dashboard/server/seo.js), so the admin preview, the public page, and search
// engines all show a post the same way.
import { Fragment } from "react";

export type NewsBlock = { kind: "heading"; level: 1 | 2 | 3; text: string } | { kind: "paragraph"; lines: string[] } | { kind: "list"; items: string[] };

export function parseNewsBody(body: string): NewsBlock[] {
  const blocks: NewsBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  const flush = () => {
    if (paragraph.length) { blocks.push({ kind: "paragraph", lines: paragraph }); paragraph = []; }
    if (list.length) { blocks.push({ kind: "list", items: list }); list = []; }
  };
  for (const raw of String(body || "").replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) { flush(); blocks.push({ kind: "heading", level: heading[1].length as 1 | 2 | 3, text: heading[2] }); continue; }
    const item = /^[-*]\s+(.+)$/.exec(line);
    if (item) { if (paragraph.length) flush(); list.push(item[1]); continue; }
    if (list.length) flush();
    paragraph.push(line);
  }
  flush();
  // Writers reach for "#" or "##" interchangeably; the biggest heading a post uses becomes
  // the first level under the title so the outline never skips a level.
  const top = Math.min(...blocks.map((block) => block.kind === "heading" ? block.level : 3));
  return blocks.map((block) => block.kind === "heading" ? { ...block, level: (block.level - top + 1) as 1 | 2 | 3 } : block);
}

/** `titleLevel` is the heading level of the post title on the page (1 on the article page). */
export function PostText({ text, titleLevel = 1, empty = "This post has no text." }: { text: string; titleLevel?: number; empty?: string }) {
  const blocks = parseNewsBody(text);
  if (!blocks.length) return <p>{empty}</p>;
  return <>{blocks.map((block, index) => {
    if (block.kind === "list") return <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>;
    if (block.kind === "paragraph") return <p key={index}>{block.lines.map((line, lineIndex) => <Fragment key={lineIndex}>{lineIndex > 0 && <br/>}{line}</Fragment>)}</p>;
    const Tag = `h${Math.min(6, titleLevel + block.level)}` as "h2" | "h3" | "h4" | "h5" | "h6";
    return <Tag key={index}>{block.text}</Tag>;
  })}</>;
}
